<template>
  <div class="home-gateway">

    <!-- ── Comută înapoi pe Mobile Hub (doar mobil, useri CRM) ──────────── -->
    <button
      v-if="isMobile && mobileHubEligible"
      type="button"
      class="home-mobilehub-switch"
      @click="switchToMobileHub"
    >
      <v-icon size="18" class="home-mobilehub-switch__icon">mdi-cellphone</v-icon>
      <span class="home-mobilehub-switch__text">{{ $t("mobileHub.switchBack") }}</span>
      <v-icon size="18">mdi-arrow-right</v-icon>
    </button>

    <!-- ── Hero ────────────────────────────────────────────────────────── -->
    <div class="home-hero">
      <div class="home-hero__left">
        <div class="home-hero__greeting">{{ greeting }}</div>
        <h1 class="home-hero__title">{{ welcomeTitle }}</h1>
        <p class="home-hero__subtitle">
          <template v-if="companyName"
            ><span class="home-hero__company">{{ companyName }}</span> · </template
          >{{ $t("home.subtitle") }}
        </p>
      </div>
      <div class="home-hero__right">
        <BrandLogo variant="lockup" aria-label="MiriDar Solutions" class="home-hero__logo" />
      </div>
    </div>

    <!-- ── Module cards ────────────────────────────────────────────────── -->
    <div v-if="visibleModules.length" class="home-modules">
      <div
        v-for="module in visibleModules"
        :key="module.id"
        class="home-card"
        :class="[`home-card--${module.id}`, { 'home-card--default': isDefaultModule(module.id) }]"
        role="button"
        tabindex="0"
        @click="goTo(module.route)"
        @keydown.enter.prevent="goTo(module.route)"
        @keydown.space.prevent="goTo(module.route)"
      >
        <!-- Accent strip top -->
        <div class="home-card__strip" />

        <div class="home-card__body">
          <!-- Icon + header -->
          <div class="home-card__header">
            <div class="home-card__icon-wrap">
              <v-icon size="26" class="home-card__icon">{{ module.icon }}</v-icon>
            </div>
            <div class="home-card__header-text">
              <div class="home-card__title">
                {{ module.titleKey ? $t(module.titleKey) : module.title }}
              </div>
              <div class="home-card__subtitle">
                {{ module.subtitleKey ? $t(module.subtitleKey) : module.subtitle }}
              </div>
            </div>
            <v-icon
              v-if="isDefaultModule(module.id)"
              size="18"
              class="home-card__star-badge"
            >
              mdi-star
            </v-icon>
          </div>

          <!-- CTA -->
          <v-btn
            class="module-cta"
            :class="`home-card__cta home-card__cta--${module.id}`"
            variant="elevated"
            @click.stop="goTo(module.route)"
          >
            {{ module.ctaKey ? $t(module.ctaKey) : module.cta }}
            <v-icon end size="18">mdi-arrow-right</v-icon>
          </v-btn>

          <!-- Default toggle -->
          <button
            class="home-card__default-toggle"
            @click.stop="toggleDefaultModule(module.id)"
          >
            <v-icon size="15">
              {{ isDefaultModule(module.id) ? "mdi-star" : "mdi-star-outline" }}
            </v-icon>
            <span>
              {{
                isDefaultModule(module.id)
                  ? $t("home.default.selected")
                  : $t("home.default.set")
              }}
            </span>
          </button>

          <!-- Quick links -->
          <div
            v-if="moduleQuickLinks(module).length"
            class="home-card__quick-links"
            @click.stop
          >
            <div class="home-card__quick-links-label">
              <span class="home-card__quick-links-line" />
              <v-icon size="13" class="home-card__quick-links-icon">mdi-lightning-bolt</v-icon>
              <span>{{ $t("home.quickLinksLabel") }}</span>
              <span class="home-card__quick-links-line" />
            </div>
            <div class="home-card__quick-links-chips">
              <button
                v-for="link in moduleQuickLinks(module)"
                :key="link.id"
                class="home-card__chip"
                :class="`home-card__chip--${module.id}`"
                @click.stop="goTo(link.route)"
              >
                {{ link.labelKey ? $t(link.labelKey) : link.label }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── No access ───────────────────────────────────────────────────── -->
    <div v-else class="home-no-access">
      <v-icon size="36" style="opacity:0.35">mdi-lock-outline</v-icon>
      <p>{{ $t("home.noAccess") }}</p>
    </div>

  </div>
</template>

<script>
import { useDisplay } from "vuetify";
import { normalizeRoleKey, HOME_DEFAULT_MODULE_KEY, resolveDefaultModuleRoute as resolveDefaultModuleTarget } from "@/Utile/roleHelpers.mjs";
import { canAccessModule, readStoredLicenseSnapshot } from "@/Utile/licenseHelpers.mjs";
import { useCurrentCompany } from "@/Utile/currentUserCompany.mjs";
import { buildHomeModules } from "@/Utile/homeModules.mjs";
import { isMobileHubEligible, MOBILE_HUB_OPT_OUT_KEY, MOBILE_HUB_ROUTE } from "@/Utile/mobileHub.mjs";
import BrandLogo from "@/components/brand/BrandLogo.vue";
import { readAuthSession } from "@/Utile/authSession.mjs";

const HOME_DEFAULT_REDIRECT_TOKEN_KEY = "home-default-redirect-token";

export default {
  name: "Home",
  components: { BrandLogo },
  setup() {
    const { companyName, userName } = useCurrentCompany();
    const { smAndDown } = useDisplay();
    return { companyName, userName, isMobile: smAndDown };
  },
  data() {
    return {
      defaultModuleId: localStorage.getItem(HOME_DEFAULT_MODULE_KEY) || "",
    };
  },
  computed: {
    greeting() {
      const h = new Date().getHours();
      if (h < 12) return this.$t("home.greetingMorning");
      if (h < 18) return this.$t("home.greetingAfternoon");
      return this.$t("home.greetingEvening");
    },
    firstName() {
      const full = (this.userName || "").trim();
      if (!full) return "";
      return full.split(/\s+/)[0];
    },
    welcomeTitle() {
      return this.firstName
        ? this.$t("home.welcomeBack", { name: this.firstName })
        : this.$t("home.title");
    },
    roleSet() {
      let parsed = [];
      try {
        parsed = JSON.parse(localStorage.getItem("user-roles") || "[]");
      } catch (e) {
        parsed = [];
      }
      const set = new Set();
      parsed.forEach((entry) => {
        if (!entry) return;
        const key = typeof entry === "string" ? entry : entry.key || entry.role || entry.name;
        const norm = normalizeRoleKey(key);
        if (norm) set.add(norm);
      });
      const legacy = normalizeRoleKey(localStorage.getItem("user-role"));
      if (legacy) set.add(legacy);
      return set;
    },
    modules() {
      const personnelRoute = this.resolveDefaultModuleRoute("vacation") || "/dashboard";
      // CRM-ul vechi a fost eliminat; toate rolurile CRM merg la /ncrm.
      const crmRoute = "/ncrm";
      return buildHomeModules({ crmRoute, personnelRoute });
    },
    visibleModules() {
      return this.modules.filter(
        (module) =>
          module.roles.some((role) => this.roleSet.has(role)) &&
          this.hasModuleAccess(module.moduleKey)
      );
    },
    licenseSnapshot() {
      return readStoredLicenseSnapshot();
    },
    mobileHubEligible() {
      return isMobileHubEligible(this.roleSet);
    },
  },
  mounted() {
    if (this.redirectToMobileHubIfNeeded()) return;
    this.redirectToDefaultModuleIfNeeded();
  },
  methods: {
    // H3 PR 2: identifies this login (session marker), never the JWT, which lives only in the httpOnly cookie.
    getCurrentSessionKey() {
      const session = readAuthSession();
      return session && !session.invalid ? session.key : "";
    },
    hasConsumedDefaultRedirect(sessionKey) {
      if (!sessionKey) return false;
      return localStorage.getItem(HOME_DEFAULT_REDIRECT_TOKEN_KEY) === sessionKey;
    },
    markDefaultRedirectConsumed(sessionKey) {
      if (!sessionKey) return;
      localStorage.setItem(HOME_DEFAULT_REDIRECT_TOKEN_KEY, sessionKey);
    },
    isDefaultModule(moduleId) {
      return this.defaultModuleId === moduleId;
    },
    resolveDefaultModuleRoute(moduleId) {
      return resolveDefaultModuleTarget(moduleId, this.roleSet, null, this.licenseSnapshot);
    },
    hasModuleAccess(moduleKey) {
      return canAccessModule(moduleKey, {
        roleKeys: Array.from(this.roleSet),
        licenseSnapshot: this.licenseSnapshot,
        fallbackIfUnknown: true,
      });
    },
    toggleDefaultModule(moduleId) {
      if (!moduleId) return;
      if (this.defaultModuleId === moduleId) {
        this.defaultModuleId = "";
        localStorage.removeItem(HOME_DEFAULT_MODULE_KEY);
        return;
      }
      this.defaultModuleId = moduleId;
      localStorage.setItem(HOME_DEFAULT_MODULE_KEY, moduleId);
      const target = this.resolveDefaultModuleRoute(moduleId);
      if (target) {
        this.markDefaultRedirectConsumed(this.getCurrentSessionKey());
        this.goTo(target);
      }
    },
    hasOptedOutMobileHub() {
      try {
        return localStorage.getItem(MOBILE_HUB_OPT_OUT_KEY) === "1";
      } catch (e) {
        return false;
      }
    },
    switchToMobileHub() {
      // Reciprocul opt-out-ului: reactivează pornirea pe mobil și deschide hub-ul.
      try {
        localStorage.removeItem(MOBILE_HUB_OPT_OUT_KEY);
      } catch (e) {
        /* ignore storage issues */
      }
      this.goTo(MOBILE_HUB_ROUTE);
    },
    redirectToMobileHubIfNeeded() {
      // Pe mobil, userii cu rol CRM pornesc pe Mobile Hub. Opt-out durabil + `?picker=1`
      // (folosit de link-ul „varianta clasică" din hub) sar peste redirect.
      if (!this.isMobile) return false;
      if (String(this.$route?.query?.picker || "") === "1") return false;
      if (this.hasOptedOutMobileHub()) return false;
      if (!this.getCurrentSessionKey()) return false;
      if (!isMobileHubEligible(this.roleSet)) return false;
      this.goTo(MOBILE_HUB_ROUTE);
      return true;
    },
    redirectToDefaultModuleIfNeeded() {
      const routeQuery = this.$route?.query || {};
      if (String(routeQuery.picker || "") === "1") return;
      const sessionKey = this.getCurrentSessionKey();
      if (!sessionKey) return;
      if (this.hasConsumedDefaultRedirect(sessionKey)) return;
      const savedModuleId = localStorage.getItem(HOME_DEFAULT_MODULE_KEY) || "";
      if (!savedModuleId) return;
      const isVisible = this.visibleModules.some((module) => module.id === savedModuleId);
      if (!isVisible) return;
      const target = this.resolveDefaultModuleRoute(savedModuleId);
      if (!target) return;
      this.markDefaultRedirectConsumed(sessionKey);
      this.goTo(target);
    },
    moduleQuickLinks(module) {
      const links = Array.isArray(module.quickLinks) ? module.quickLinks : [];
      const visible = links.filter((link) => {
        if (!Array.isArray(link.roles) || !link.roles.length) return true;
        return link.roles.some((role) => this.roleSet.has(role));
      });
      return visible.slice(0, 3);
    },
    goTo(target) {
      this.$router.push(target);
    },
  },
};
</script>

<style scoped src="./Home.styles.css"></style>
